import React, { useState } from 'react'
import { FaCopy, FaEllipsisH, FaArchive, FaEnvelope, FaExclamationTriangle, FaSearch } from 'react-icons/fa'
import { PiArrowBendUpRightBold } from 'react-icons/pi'
import { Link } from 'react-router'
import { Container, Stack, Grid, Text, Button, Loading, Icon, Heading, Box, Badge, InputGroup, Input, EmptyCard, Stat, IconButton, Menu, SimpleMenu } from '@/components/lib'
import api from '../../api'
import useStore from '../../store'
import utils from '../../utils/utils.js'
import useTitle from '../../hooks/useTitle'

import EmptyBox from '../includes/EmptyBox'
import ConfirmButton from '../includes/ConfirmButton'
import Login from '../entry/Login'

import PlansImage from '../../images/plans.png'
import NotFoundImage from '../../images/notFound.png'

export default function TeamTrials () {
  const [filter, setFilter] = useState('')
  const [, setCopying] = useState(false)
  const {
    user,
    trials,
    loadingTrials,
    listTrialsIncludeArchived,
    setListTrialsIncludeArchived,
    updateTrial
  } = useStore()

  useTitle('Trials')

  function clone (trial) {
    setCopying(true)
    api.teams.createTrial(user.team, { copyFrom: trial._id }, newTrial => {
      setCopying(false)
      updateTrial(newTrial._id, newTrial)
      utils.toast('success', 'Trial copied successfully')
    }, err => {
      setCopying(false)
      utils.toast('error', 'Failed to copy trial', err.message)
    })
  }

  function setTrialArchived (trial, isArchived) {
    api.trials.update(
      trial._id,
      { isArchived },
      () => {
        utils.toast('success', isArchived ? 'Trial archived' : 'Trial unarchived')
        updateTrial(trial._id, { isArchived })
      },
      err => utils.toast('error', 'Could not update trial', err.message)
    )
  }

  let filtered
  if (filter) {
    const exp = new RegExp(filter, 'i')
    filtered = filter && trials.filter(t => t.name.match(exp))
  } else filtered = trials
  if (!listTrialsIncludeArchived) filtered = filtered.filter(t => !t.isArchived)

  let allPermissions = 0
  Object.keys(user?.permissions || {})?.forEach(scope => {
    allPermissions += user.permissions[scope]?.length || 0
  })

  if (!user) return <Login withWarning />

  return (
    <Container>
      <Stack gap={5} m='auto' maxW='900px'>
        {trials.length > 0 &&
          <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={3}>
            <Heading as='h2' size='lg'>Your projects</Heading>
            <Stack direction='row' alignItems='center' gap={1}>
              <InputGroup w='200px' leftIcon={<Icon as={FaSearch} color='fg.subtle' />}>
                <Input placeholder='Filter...' value={filter} onChange={e => setFilter(e.target.value)} />
              </InputGroup>
              <SimpleMenu
                trigger={
                  <IconButton
                    aria-label='List options'
                    variant='ghost'
                    size='sm'
                  >
                    <Icon as={FaEllipsisH} />
                  </IconButton>
                }
                items={[
                  utils.hasPermission(user, 'trials.create', user.team) && {
                    text: 'Create a new trial',
                    onClick: () => { },
                    as: Link,
                    to: '/trials/new',
                    value: 'createTrial'
                  },
                  {
                    text: listTrialsIncludeArchived ? 'Hide archived trials' : 'Show archived trials',
                    onClick: () => setListTrialsIncludeArchived(!listTrialsIncludeArchived),
                    value: listTrialsIncludeArchived ? 'hideArchivedTrials' : 'showArchivedTrials'
                  }
                ].filter(Boolean)}
              />
            </Stack>
          </Box>}

        {loadingTrials && !trials?.length && <Box m={10} textAlign='center'><Loading /></Box>}
        {!loadingTrials && !trials?.length &&
          <>
            {utils.hasPermission(user, 'trials.create', user.team) &&
              <Stack bg='green.50' _dark={{ bg: 'green.950' }} p={3} rounded='md' shadow='md' display='flex' flexDirection='column' justifyContent='center'>
                <Heading size='sm'>Do you need help getting started?</Heading>
                <Text>Check out our Getting Started guide on how to get the most out of Trialflare.</Text>
                <Button colorPalette='green' as='a' href='https://help.trialflare.com/getting-started' target='_blank'>View Guide</Button>
              </Stack>}
            <EmptyBox
              image={PlansImage} title={'You don\'t have any trials yet'}
              description={allPermissions === 0 && 'You may need to ask your account manager for permission or you will need to be a collaborator on a trial.'}
            >
              {utils.hasPermission(user, 'trials.create', user.team) && <Button colorPalette='primary' as={Link} to='/trials/new'>Create your first trial</Button>}
              {!listTrialsIncludeArchived && (
                <Button variant='ghost' mt={2} onClick={() => setListTrialsIncludeArchived(true)}>
                  Show archived trials
                </Button>
              )}
            </EmptyBox>
          </>}

        {trials?.length > 0 && !filtered?.length &&
          <EmptyBox image={NotFoundImage} title='Your search returned no results' description='Try changing your search terms.' />}

        {filtered?.length > 0 &&
          <Grid templateColumns={{ md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4} mt={5}>
            {utils.hasPermission(user, 'trials.create', user.team) &&
              <EmptyCard
                compact icon='research'
                title='Create a new trial'
                description='Start a new project using the button below.'
              >
                <Button colorPalette='primary' as={Link} to='/trials/new'>Create a trial</Button>
              </EmptyCard>}
            {filtered.map(t =>
              <Stack
                key={t._id}
                rounded='md' p={3} width='100%' justifyContent='space-between'
                bg='bg.surface'
                borderWidth='1px' borderColor='border.default'
                shadow='sm'
                transition='0.25s'
                _hover={{ shadow: 'md', borderColor: 'border.emphasized' }}
              >
                <Stack>
                  <Heading size='sm'><Link to={`/trials/${t._id}/manage`}>{t.name}</Link></Heading>
                  <Stack direction='row' alignItems='center' gap={2} flexWrap='wrap'>
                    {utils.canManageTrial(user, t) && <Badge colorPalette='blue'>You're an admin</Badge>}
                    {t.isArchived && <Badge colorPalette='gray'>Archived</Badge>}
                    <Text color='fg.subtle' fontSize='xs'>
                      Created {utils.formatDate(t.createdAt)}
                    </Text>
                  </Stack>
                </Stack>

                <Stack flex={1}>
                  {(t.dataTypeCount > 0 && t.stageCount > 0) &&
                    <Stack direction='row' justifyContent='space-between' alignItems='center' p={2} bg='bg.subtle' rounded='md' borderWidth='1px' borderColor='border.muted'>
                      <Stat
                        label='Participants'
                        value={t.participantCount}
                      />
                      <Stat
                        label='Responses'
                        value={t.responseCount}
                      />
                    </Stack>}
                </Stack>

                {t.dataTypeCount === 0 && utils.canManageTrial(user, t) &&
                  <>
                    <Box borderBottomWidth='1px' borderColor='border.muted' />
                    <Stack justifyContent='space-between' alignItems='center' direction='row' as={Link} to={`/trials/${t._id}/data`} color='primary.fg' fontSize='sm'>
                      <Stack direction='row' alignItems='center'>
                        <Icon as={FaExclamationTriangle} color='red.500' _dark={{ color: 'red.400' }} />
                        <Text>Set-up data types</Text>
                      </Stack>
                      <Icon as={PiArrowBendUpRightBold} />
                    </Stack>
                  </>}
                {t.stageCount === 0 && utils.canManageTrial(user, t) &&
                  <>
                    <Box borderBottomWidth='1px' borderColor='border.muted' />
                    <Stack justifyContent='space-between' alignItems='center' direction='row' as={Link} to={`/trials/${t._id}/stages`} color='primary.fg' fontSize='sm'>
                      <Stack direction='row' alignItems='center'>
                        <Icon as={FaExclamationTriangle} color='red.500' _dark={{ color: 'red.400' }} />
                        <Text>Set-up stages</Text>
                      </Stack>
                      <Icon as={PiArrowBendUpRightBold} />
                    </Stack>
                  </>}
                {t.hasUnreadMessages && utils.canSupportTrial(user, t) &&
                  <>
                    <Box borderBottomWidth='1px' borderColor='border.muted' />
                    <Stack justifyContent='space-between' alignItems='center' direction='row' as={Link} to={`/trials/${t._id}/messages`} color='primary.fg' fontSize='sm'>
                      <Stack direction='row' alignItems='center'>
                        <Icon as={FaEnvelope} color='blue.500' _dark={{ color: 'blue.300' }} />
                        View unread messages
                      </Stack>
                      <Icon as={PiArrowBendUpRightBold} />
                    </Stack>
                  </>}

                <Box borderBottomWidth='1px' borderColor='border.muted' />
                <Box display='flex' justifyContent='space-between'>
                  {utils.canAccessTrial(user, t) &&
                    <Button as={Link} to={`/trials/${t._id}`} colorPalette='primary' size='xs'>
                      Open
                    </Button>}
                  {(utils.canReadTrial(user, t) || utils.canManageTrial(user, t)) &&
                    <Menu
                      trigger={
                        <IconButton aria-label='Trial actions' variant='ghost' size='xs'>
                          <Icon as={FaEllipsisH} />
                        </IconButton>
                      }
                    >
                      {utils.canReadTrial(user, t) && (
                        <ConfirmButton
                          asSubMenu
                          trigger={
                            <Stack direction='row' alignItems='center' gap={2}>
                              <Icon as={FaCopy} />
                              Make a copy
                            </Stack>
                          }
                          header='Make a copy of this trial'
                          body={
                            <Text>A new trial will be created with the same configuration as this one. Note that only data types that are being used in stages will be copied.</Text>
                          }
                          onConfirm={() => clone(t)}
                        />
                      )}
                      {utils.canManageTrial(user, t) && (
                        <ConfirmButton
                          asSubMenu
                          trigger={
                            <Stack direction='row' alignItems='center' gap={2}>
                              <Icon as={FaArchive} />
                              {t.isArchived ? 'Unarchive this trial' : 'Archive this trial'}
                            </Stack>
                          }
                          header={t.isArchived ? 'Unarchive this trial?' : 'Archive this trial?'}
                          body={
                            <Text>
                              {t.isArchived
                                ? 'This trial will appear again in your default trial list.'
                                : 'Archived trials are hidden from the default list. You can show them anytime from the list options menu.'}
                            </Text>
                          }
                          onConfirm={() => setTrialArchived(t, !t.isArchived)}
                        />
                      )}
                    </Menu>}
                </Box>
              </Stack>
            )}
          </Grid>}
      </Stack>
    </Container>
  )
}
